import { Suspense, lazy } from "react";
import { motion, MotionConfig } from "framer-motion";
import { Brain, MessageSquare, Target, ArrowRight } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { DashboardPreview } from "../components/DashboardPreview";
import { MascotFallback } from "../components/MascotViewer/MascotFallback";
import { useInViewOnce } from "../../../hooks/useInViewOnce";
import mascoteHologramaUrl from "../../../assets/mascote3d/MascoteComOlograma.glb?url";
import neurinhoSectionSvg from "../../../assets/mascote3d/NeurinhoSection.svg";

const LazyMascotViewer = lazy(() =>
  import("../components/MascotViewer").then((m) => ({
    default: m.MascotViewer,
  }))
);

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const AI_PILLARS = [
  {
    icon: Brain,
    title: "Classificação por regras claras",
    text: 'Questionário e comportamento alimentam um perfil canônico e decisões auditáveis — sem "IA caixa-preta" no núcleo do MVP.',
  },
  {
    icon: MessageSquare,
    title: "Explicação no seu formato",
    text: "Mais visual, mais textual ou mais guiado: a mesma ideia central, entregue do jeito que o perfil pede.",
  },
  {
    icon: Target,
    title: "Ritmo que reage ao que você faz",
    text: "Tempo na tela, erros e acertos ajustam ênfase e revisão — com limiares parametrizáveis, não achismo.",
  },
];

interface AiSectionProps {
  onDemoCta: () => void;
}

export function AiSection({ onDemoCta }: AiSectionProps) {
  const { ref, isVisible } = useInViewOnce();

  return (
    <MotionConfig reducedMotion="user">
      <section
        id="ia"
        ref={ref}
        className="scroll-mt-20 border-b border-white/[0.06] py-16 sm:scroll-mt-24 sm:py-20 lg:py-24"
      >
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">

          {/* Mobile mascote — SVG leve, sem WebGL */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, ease: SPRING }}
            className="mb-6 flex justify-center md:hidden"
          >
            <img
              src={neurinhoSectionSvg}
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              className="h-64 w-64 object-contain will-change-transform drop-shadow-[0_0_48px_rgba(45,212,191,0.35)]"
            />
          </motion.div>

          {/* Hero grid: pillars (left) + mascote (right) */}
          <div className="mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-2 md:gap-12">

            {/* Left — pillars */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={isVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.65, ease: SPRING }}
              className="min-w-0"
            >
              <Badge className="mb-4 border-violet-500/30 bg-violet-500/10 text-violet-200">
                Personalização e assistência
              </Badge>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl">
                Regras e perfil primeiro;{" "}
                <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">
                  IA como apoio
                </span>{" "}
                — não atalho para cola.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
                No núcleo do MVP, decisões de apresentação e ritmo vêm de algoritmos
                determinísticos e parâmetros configuráveis. A IA entra para explicar,
                sugerir e desbloquear dúvidas — sempre alinhada ao que a instituição
                definiu.
              </p>
              <div className="mt-8 space-y-4">
                {AI_PILLARS.map((item) => (
                  <div
                    key={item.title}
                    className="card-lift flex gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 backdrop-blur-sm sm:gap-4 sm:p-4"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600">
                      <item.icon className="h-5 w-5 text-white" aria-hidden />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right — mascote 3D (desktop only) */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.75, delay: 0.15, ease: SPRING }}
              className="hidden items-center justify-center md:flex"
            >
              <Suspense
                fallback={<MascotFallback animationType="breathe" size={380} />}
              >
                <LazyMascotViewer
                  glbUrl={mascoteHologramaUrl}
                  animationType="breathe"
                  size={580}
                  rotationY={4.65}
                />
              </Suspense>
            </motion.div>
          </div>

          {/* Study plan card — full-width below the hero grid */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.25, ease: SPRING }}
            className="mx-auto mt-16 max-w-3xl sm:mt-20 lg:mt-24"
          >
            <Card className="border border-teal-500/20 bg-gradient-to-br from-teal-950/50 to-slate-900/80 p-5 backdrop-blur-xl sm:p-8">
              <div className="mb-6 flex flex-wrap items-center gap-3">
                <div className="rounded-xl bg-teal-500/20 p-3">
                  <Target className="h-8 w-8 text-teal-300" aria-hidden />
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold text-white">
                    Prova marcada → plano nasce sozinho
                  </h3>
                  <p className="text-sm text-teal-200/70">
                    Preparação inteligente até a data
                  </p>
                </div>
              </div>
              <ol className="space-y-4 text-sm text-slate-300">
                {[
                  "Professor publica prova, conteúdos e materiais de apoio.",
                  "O sistema monta trilha de estudo com prazos intermediários.",
                  'O sistema ajusta ênfase e ritmo conforme acertos, erros e tempo (regras claras, não "feeling").',
                  "Pais e coordenação acompanham risco e engajamento — sem surpresa de última hora.",
                ].map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-teal-300">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              <Button
                onClick={onDemoCta}
                className="landing-cta-glow mt-6 w-full bg-gradient-to-r from-teal-500 to-cyan-600 font-semibold hover:from-teal-400 hover:to-cyan-500 sm:mt-8 sm:w-auto"
              >
                Solicitar demonstração
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
              </Button>
            </Card>
          </motion.div>

          {/* Grade simulator */}
          <DashboardPreview onDemoCta={onDemoCta} />
        </div>
      </section>
    </MotionConfig>
  );
}
